import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { getTrucksApi } from "../api/trucksApi";
import { getUsersApi } from "../api/usersApi";
import { getCitiesApi } from "../api/citiesApi";
import {
  linkLogislyDriverApi,
  linkLogislyLocationApi,
  linkLogislyTruckApi,
  searchLogislyDriversApi,
  searchLogislyLocationsApi,
  searchLogislyTrucksApi,
} from "../api/logislyOrderTakerApi";
import type {
  LogislyDriver,
  LogislyLinkStatus,
  LogislyLocation,
  LogislyTruck,
} from "../types/logislyOrderTaker";
import type { Truck } from "../types/truck";
import type { City } from "../types/city";
import { type User, UserRole } from "../types/user";
import SourceChip from "../components/common/SourceChip";
import TablePagination from "../components/common/TablePagination";
import type { PaginationParam, PaginationResponse } from "../types/paginationResponse";

const defaultPagination: PaginationParam = { page: 1, pageSize: 50 };

const LinkStatusChip = ({
  status,
  issue,
}: {
  status?: LogislyLinkStatus;
  issue?: string;
}) => {
  const normalized = status ?? "UNLINKED";
  const color =
    normalized === "LINKED"
      ? "success"
      : normalized === "CONFLICT"
        ? "error"
        : normalized === "UNLINKED"
          ? "default"
          : "warning";
  const label =
    normalized === "MISSING_CATALOG"
      ? "Missing catalog"
      : normalized === "MISSING_PRIMARY"
        ? "Missing primary"
        : normalized.replace("_", " ").toLowerCase();
  return <Chip size="small" label={issue ? `${label}: ${issue}` : label} color={color} />;
};

export const LogislyTrucksPage: React.FC = () => {
  const [logislyTrucks, setLogislyTrucks] = useState<LogislyTruck[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [pagination, setPagination] = useState<PaginationParam>(defaultPagination);
  const [paginationResponse, setPaginationResponse] = useState<PaginationResponse | null>(null);

  const load = useCallback(async () => {
    const [logislyData, localData] = await Promise.all([
      searchLogislyTrucksApi(pagination),
      getTrucksApi(),
    ]);
    setLogislyTrucks(logislyData.items);
    setPaginationResponse(logislyData.pagination);
    setTrucks(localData);
  }, [pagination]);

  useEffect(() => {
    load().catch((error) => console.error("Failed to load Logisly trucks:", error));
  }, [load]);

  const truckById = useMemo(() => new Map(trucks.map((truck) => [truck._id, truck])), [trucks]);

  const handleLink = async (id: string, event: SelectChangeEvent<string>) => {
    await linkLogislyTruckApi(id, event.target.value);
    await load();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Logisly Trucks
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Plate</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Driver Snapshot</TableCell>
            <TableCell>Linked Truck</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logislyTrucks.map((item) => {
            const linked =
              item.local ?? (item.localTruckId ? truckById.get(item.localTruckId) : undefined);
            return (
              <TableRow key={item._id}>
                <TableCell>{item.number}</TableCell>
                <TableCell>{item.shortName || item.name || "-"}</TableCell>
                <TableCell>{item.driver?.main?.name || "-"}</TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Truck</InputLabel>
                    <Select
                      label="Truck"
                      value={item.localTruckId || ""}
                      onChange={(event) => handleLink(item._id, event)}
                    >
                      {trucks.map((truck) => (
                        <MenuItem key={truck._id} value={truck._id}>
                          {truck.licensePlate} - {truck.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <LinkStatusChip status={item.linkStatus} issue={item.linkIssue} />
                </TableCell>
                <TableCell>
                  <SourceChip source={linked?.initiallyCreatedFrom} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {paginationResponse && (
        <TablePagination
          count={paginationResponse.total}
          pagination={pagination}
          onPaginationChange={setPagination}
          showRowsPerPage
        />
      )}
    </Box>
  );
};

export const LogislyDriversPage: React.FC = () => {
  const [logislyDrivers, setLogislyDrivers] = useState<LogislyDriver[]>([]);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationParam>(defaultPagination);
  const [paginationResponse, setPaginationResponse] = useState<PaginationResponse | null>(null);

  const load = useCallback(async () => {
    const [logislyData, users] = await Promise.all([
      searchLogislyDriversApi(pagination),
      getUsersApi(),
    ]);
    setLogislyDrivers(logislyData.items);
    setPaginationResponse(logislyData.pagination);
    setDrivers(users.filter((user) => user.role === UserRole.DRIVER));
  }, [pagination]);

  useEffect(() => {
    load().catch((error) => console.error("Failed to load Logisly drivers:", error));
  }, [load]);

  const driverById = useMemo(
    () => new Map(drivers.map((driver) => [driver._id, driver])),
    [drivers],
  );

  const handleLink = async (id: string, event: SelectChangeEvent<string>) => {
    await linkLogislyDriverApi(id, event.target.value);
    await load();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Logisly Drivers
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Linked Driver</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logislyDrivers.map((item) => {
            const linked =
              item.local ?? (item.localUserId ? driverById.get(item.localUserId) : undefined);
            return (
              <TableRow key={item._id}>
                <TableCell>{item.name || "-"}</TableCell>
                <TableCell>{item.phone || "-"}</TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Driver</InputLabel>
                    <Select
                      label="Driver"
                      value={item.localUserId || ""}
                      onChange={(event) => handleLink(item._id, event)}
                    >
                      {drivers.map((driver) => (
                        <MenuItem key={driver._id} value={driver._id}>
                          {driver.name || driver._id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <LinkStatusChip status={item.linkStatus} issue={item.linkIssue} />
                </TableCell>
                <TableCell>
                  <SourceChip source={linked?.initiallyCreatedFrom} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {paginationResponse && (
        <TablePagination
          count={paginationResponse.total}
          pagination={pagination}
          onPaginationChange={setPagination}
          showRowsPerPage
        />
      )}
    </Box>
  );
};

export const LogislyLocationsPage: React.FC = () => {
  const [logislyLocations, setLogislyLocations] = useState<LogislyLocation[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [pagination, setPagination] = useState<PaginationParam>(defaultPagination);
  const [paginationResponse, setPaginationResponse] = useState<PaginationResponse | null>(null);

  const load = useCallback(async () => {
    const [logislyData, localData] = await Promise.all([
      searchLogislyLocationsApi(pagination),
      getCitiesApi(),
    ]);
    setLogislyLocations(logislyData.items);
    setPaginationResponse(logislyData.pagination);
    setCities(localData);
  }, [pagination]);

  useEffect(() => {
    load().catch((error) => console.error("Failed to load Logisly locations:", error));
  }, [load]);

  const cityById = useMemo(() => new Map(cities.map((city) => [city._id, city])), [cities]);

  const handleLink = async (id: string, event: SelectChangeEvent<string>) => {
    await linkLogislyLocationApi(id, event.target.value);
    await load();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Logisly Locations
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Location</TableCell>
            <TableCell>Area</TableCell>
            <TableCell>Linked Location</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logislyLocations.map((item) => {
            const linked =
              item.local ?? (item.localCityId ? cityById.get(item.localCityId) : undefined);
            return (
              <TableRow key={item._id}>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  {[item.address?.level_2, item.address?.level_3].filter(Boolean).join(", ") || "-"}
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Location</InputLabel>
                    <Select
                      label="Location"
                      value={item.localCityId || ""}
                      onChange={(event) => handleLink(item._id, event)}
                    >
                      {cities.map((city) => (
                        <MenuItem key={city._id} value={city._id}>
                          {city.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <LinkStatusChip status={item.linkStatus} issue={item.linkIssue} />
                </TableCell>
                <TableCell>
                  <SourceChip source={linked?.initiallyCreatedFrom} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {paginationResponse && (
        <TablePagination
          count={paginationResponse.total}
          pagination={pagination}
          onPaginationChange={setPagination}
          showRowsPerPage
        />
      )}
    </Box>
  );
};
